import React, { useEffect } from 'react';
import { Avatar, Box, Button, CircularProgress, IconButton, Typography, Stack, TextField, Switch, FormControlLabel } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useProfile from '../hooks/UseProfile';
import useSaveProfile from '../hooks/UserSaveProfile';
import LoadingInside from 'core/components/shared/LoadingInside';
import SelectCountry from 'core/components/shared/select/SelectCountry';
import { HttpService } from 'core/services/http';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import useProfilePhotoCropFlow from '../hooks/useProfilePhotoCropFlow';
import useProfileHeaderUpload from '../hooks/useProfileHeaderUpload';
import {
  defaultProfileHeaderGradient,
  getProfileHeaderUrl,
  getProfileAvatarUrl,
  getProfileDisplayName,
  getProfileAvatarLetter,
  nameToAvatarColor,
} from 'features/feature_job/utils/profileDisplay';

const ProfileEditScreen = ({ onCancel }) => {
  const { data: profile, isLoading: profileLoading, refetch } = useProfile();
  const user = HttpService.getUser();
  const { mutate: saveProfile, isLoading: saving } = useSaveProfile();
  const { enqueueSnackbar } = useSnackbar();
  const { fileInputRef, handleFileChange, cropDialog } = useProfilePhotoCropFlow({
    profile,
    onUploaded: () => refetch(),
    enqueueSnackbar,
  });

  const {
    inputRef: headerInputRef,
    uploading: headerUploading,
    pickFile: pickHeaderFile,
    onFileChange: onHeaderFileChange,
    removeHeader,
  } = useProfileHeaderUpload({
    profile,
    onDone: () => refetch(),
    enqueueSnackbar,
  });

  // Initialize react-hook-form with default values
  const { register, handleSubmit, reset, control, watch, setValue } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      homeAddress: '',
      postalCode: '',
      city: '',
      country: '',
      phoneNumber: '',
      about: '',
      readyToWork: true,
    },
  });

  // Once profile data is loaded, update the form fields
  useEffect(() => {
    if (profile) {
      reset({
        firstName: profile.first_name || '',
        lastName: profile.last_name || '',
        homeAddress: profile.home_address || '',
        postalCode: profile.postalcode || '',
        city: profile.city || '',
        country: profile.country_id ? profile.country_id.toString() : '',
        phoneNumber: profile.phone_number || '',
        about: profile.about || '',
        readyToWork: profile.ready_to_work !== false,
      });
    }
  }, [profile, reset]);

  // Handle form submission
  const onSubmit = (data) => {
    const body = {
      id: profile.id,
      first_name: data.firstName,
      last_name: data.lastName,
      home_address: data.homeAddress,
      postalcode: data.postalCode,
      city: data.city,
      phone_number: data.phoneNumber,
      about: data.about,
      ready_to_work: data.readyToWork,
    };

    if (data.country) {
      body.country_id = data.country;
    }

    saveProfile(body, {
      onSuccess: () => {
        enqueueSnackbar('Changes saved successfully', { variant: 'success' });
        refetch();
      },
      onError: () => {
        enqueueSnackbar('Failed to save changes', { variant: 'error' });
      },
    });
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      className="mb-5 p-3"
    >
      <Box
        p={3}
        bgcolor="white"
        boxShadow={2}
        borderRadius={2}
        width="100%"
      >
        {/* Header with Back Button */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          {onCancel && (
            <IconButton
              onClick={onCancel}
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h5" fontWeight={600}>
            Edit Profile
          </Typography>
        </Stack>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFileChange}
        />
        <input
          ref={headerInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={onHeaderFileChange}
        />
        {cropDialog}
        <LoadingInside loading={profileLoading} />
        <form className="col-xl-9 col-md-12 mx-auto" onSubmit={handleSubmit(onSubmit)}>
          <div className="row wow animate__animated animate__fadeInUp" data-wow-delay=".1s">
            <div className="col-lg-12 col-md-12" style={{ marginBottom: 16 }}>
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: 100, sm: 120 },
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: getProfileHeaderUrl(profile)
                      ? undefined
                      : defaultProfileHeaderGradient(profile?.id ?? profile?.user),
                    backgroundImage: getProfileHeaderUrl(profile)
                      ? `url(${getProfileHeaderUrl(profile)})`
                      : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ position: 'absolute', bottom: 8, right: 8, alignItems: 'center' }}
                >
                  {headerUploading && <CircularProgress size={20} />}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={pickHeaderFile}
                    disabled={headerUploading}
                    sx={{
                      textTransform: 'none',
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'common.white',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                    }}
                  >
                    {profile?.header_image ? 'Change cover' : 'Add cover'}
                  </Button>
                  {profile?.header_image ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={removeHeader}
                      disabled={headerUploading}
                      sx={{
                        textTransform: 'none',
                        borderColor: 'rgba(255,255,255,0.85)',
                        color: 'common.white',
                      }}
                    >
                      Remove
                    </Button>
                  ) : null}
                </Stack>
              </Box>
            </div>
            <div className="col-lg-12 col-md-12 text-center">
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ position: 'relative', width: 100, height: 100, mx: 'auto' }}>
                  <Avatar
                    alt={getProfileDisplayName(profile, user)}
                    src={getProfileAvatarUrl(profile) || undefined}
                    component="button"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    aria-label="Change profile photo"
                    sx={{
                      width: 100,
                      height: 100,
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: 'divider',
                      p: 0,
                      fontSize: '2rem',
                      fontWeight: 700,
                      bgcolor: nameToAvatarColor(getProfileDisplayName(profile, user)),
                      color: 'common.white',
                      '&:hover': { opacity: 0.92, borderColor: 'text.disabled' },
                    }}
                  >
                    {getProfileAvatarLetter(profile, user)}
                  </Avatar>
                  <IconButton
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                    aria-label="Upload profile photo"
                    sx={{
                      position: 'absolute',
                      bottom: -4,
                      right: -4,
                      bgcolor: 'background.paper',
                      color: 'text.primary',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: 1,
                      '&:hover': { bgcolor: 'action.hover', borderColor: 'text.secondary' },
                    }}
                  >
                    <PhotoCameraIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>
            </div>
            <div className="col-lg-6 col-md-6">
              <div className="input-style mb-20">
                <input 
                  {...register('firstName')} 
                  placeholder="First Name" 
                  type="text" 
                />
              </div>
            </div>
            <div className="col-lg-6 col-md-6">
              <div className="input-style mb-20">
                <input 
                  {...register('lastName')} 
                  placeholder="Last Name" 
                  type="text" 
                />
              </div>
            </div>
            <div className="col-lg-6 col-md-6">
              <div className="input-style mb-20">
                <Controller
                  name="country"
                  control={control}
                  render={({ field }) => (
                    <SelectCountry 
                      {...field} 
                      title="Select Country"
                    />
                  )}
                />
              </div>
            </div>
            <div className="col-lg-6 col-md-6">
              <div className="input-style mb-20">
                <input 
                  {...register('city')} 
                  placeholder="City" 
                  type="text" 
                />
              </div>
            </div>
            <div className="col-lg-6 col-md-6">
              <div className="input-style mb-20">
                <input 
                  {...register('homeAddress')} 
                  placeholder="Street Address" 
                  type="text" 
                />
              </div>
            </div>
            <div className="col-lg-6 col-md-6">
              <div className="input-style mb-20">
                <input 
                  {...register('postalCode')} 
                  placeholder="Postal Code" 
                  type="text" 
                />
              </div>
            </div>
            <div className="col-lg-6 col-md-6">
              <div className="input-style mb-20">
                <input 
                  {...register('phoneNumber')} 
                  placeholder="Phone Number" 
                  type="tel" 
                />
              </div>
            </div>
            <div className="col-12">
              <FormControlLabel
                control={
                  <Switch
                    checked={watch('readyToWork')}
                    onChange={(e) => setValue('readyToWork', e.target.checked, { shouldDirty: true })}
                    color="primary"
                  />
                }
                label="Open to work (show employers I am available)"
                sx={{ mb: 2, display: 'block' }}
              />
              <TextField
                {...register('about')}
                label="About me"
                placeholder="Tell employers about yourself"
                multiline
                minRows={4}
                fullWidth
                variant="outlined"
                sx={{ mb: 3 }}
              />
              <Stack direction="row" spacing={2}>
                {onCancel && (
                  <Button
                    variant="outlined"
                    onClick={onCancel}
                    disabled={saving}
                    size="large"
                    sx={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={saving}
                  size="large"
                  sx={{ flex: onCancel ? 1 : 'none', width: onCancel ? 'auto' : '100%' }}
                >
                  {saving ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
                </Button>
              </Stack>
            </div>
          </div>
        </form>
      </Box>
    </Box>
  );
};

export default ProfileEditScreen;
